import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface DurationTimerProps {
  targetBehavior: string;
  onSave?: (duration: number, notes: string) => void;
}

type TimerState = 'idle' | 'running' | 'paused';

// ═══════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════

const DurationTimer: React.FC<DurationTimerProps> = ({
  targetBehavior,
  onSave,
}) => {
  const [state, setState] = useState<TimerState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [notes, setNotes] = useState('');

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (state === 'running') {
      interval = setInterval(() => {
        setElapsed((prev) => prev + 1000);
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [state]);

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const formatTimeDisplay = (ms: number): { hours: string; minutes: string; seconds: string } => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };
  };

  const handleStart = useCallback(() => {
    setState('running');
    if (!startTime) {
      setStartTime(new Date());
    }
  }, [startTime]);

  const handlePause = useCallback(() => {
    setState('paused');
  }, []);

  const handleResume = useCallback(() => {
    setState('running');
  }, []);

  const handleStop = useCallback(() => {
    setState('idle');
    setShowSaveDialog(true);
  }, []);

  const handleReset = useCallback(() => {
    setState('idle');
    setElapsed(0);
    setStartTime(null);
    setNotes('');
  }, []);

  const handleSave = useCallback(() => {
    const durationMinutes = Math.floor(elapsed / 1000 / 60);
    
    if (onSave) {
      onSave(durationMinutes, notes);
    }
    
    handleReset();
    setShowSaveDialog(false);
  }, [elapsed, notes, onSave, handleReset]);

  const timeDisplay = formatTimeDisplay(elapsed);

  return (
    <>
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Duration Timer
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Target: {targetBehavior}
        </Typography>

        {/* Large Timer Display */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'baseline',
            gap: 1,
            mb: 3,
            py: 2,
            backgroundColor: state === 'running' ? 'success.light' : 'grey.100',
            borderRadius: 2,
          }}
        >
          <Typography 
            variant="h1" 
            fontWeight={700}
            sx={{ 
              fontVariantNumeric: 'tabular-nums',
              color: state === 'running' ? 'success.contrastText' : 'text.primary'
            }}
          >
            {timeDisplay.hours !== '00' && `${timeDisplay.hours}:`}
            {timeDisplay.minutes}:{timeDisplay.seconds}
          </Typography>
        </Box>

        {/* Status Chip */}
        <Box sx={{ mb: 3 }}>
          <Chip
            icon={<TimerIcon />}
            label={state === 'idle' ? 'Ready' : state === 'running' ? 'Timing...' : 'Paused'}
            color={state === 'running' ? 'success' : state === 'paused' ? 'warning' : 'default'}
            size="medium"
          />
        </Box>

        {/* Control Buttons */}
        <Grid container spacing={2} justifyContent="center">
          {state === 'idle' && (
            <Grid item>
              <Button
                variant="contained"
                size="large"
                startIcon={<StartIcon />}
                onClick={handleStart}
                color="success"
              >
                Start
              </Button>
            </Grid>
          )}

          {state === 'running' && (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PauseIcon />}
                  onClick={handlePause}
                  color="warning"
                >
                  Pause
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                  color="error"
                >
                  Stop & Save
                </Button>
              </Grid>
            </>
          )}

          {state === 'paused' && (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<StartIcon />}
                  onClick={handleResume}
                  color="success"
                >
                  Resume
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                  color="error"
                >
                  Stop & Save
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        {/* Cancel Button */}
        {state !== 'idle' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleReset}
              color="inherit"
            >
              Cancel & Reset
            </Button>
          </Box>
        )}
      </Paper>

      {/* Save Dialog */}
      <Dialog 
        open={showSaveDialog} 
        onClose={() => setShowSaveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Duration Data</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Behavior:</strong> {targetBehavior}
            </Typography>
            <Typography variant="body1">
              <strong>Duration:</strong> {formatTime(elapsed)}
            </Typography>
            <Typography variant="body1">
              <strong>Started:</strong> {startTime?.toLocaleTimeString()}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Context about this duration measurement..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Data
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DurationTimer;
